import React, { useState, useEffect } from 'react';
import { perfilApi } from '../../../../api';
import './Perfil.css';
import { useAuth } from '../../../../context/AuthContext';

const ESTADO_CIVIL = ['Soltero', 'Casado', 'Divorciado', 'Viudo', 'Conviviente'];
const SITUACION = ['Empleado', 'Independiente', 'Desempleado', 'Jubilado', 'Estudiante'];
const SEXO = [{ value: 'M', label: 'Masculino' }, { value: 'F', label: 'Femenino' }];
const TIPO_EMPLEADO = ['Dependiente', 'Independiente', 'Microempresario', 'Otro'];

// Campos obligatorios para considerar el perfil "completo"
const REQUIRED_FIELDS = [
  'nombre', 'apellido', 'email', 'telefono', 'direccion',
  'departamento', 'provincia', 'distrito',
  'estado_civil', 'situacion_laboral',
];

export function isPerfilCompleto(persona) {
  if (!persona) return false;
  return REQUIRED_FIELDS.every((f) => persona[f] && String(persona[f]).trim() !== '');
}

const Perfil = () => {
  const { updateUser } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tab, setTab] = useState('datos');
  const [pwForm, setPwForm] = useState({ password_actual: '', nueva_password: '', confirmar: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [completo, setCompleto] = useState(false);

  useEffect(() => {
    perfilApi.getPerfil()
      .then((data) => {
        setPerfil(data);
        const persona = data.persona || {};
        setForm(persona);
        setCompleto(isPerfilCompleto(persona));
      })
      .catch((err) => {
        if (err.status !== 401) {
          setError(err.message);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    // Validar campos obligatorios antes de enviar
    const faltantes = REQUIRED_FIELDS.filter((f) => !form[f] || String(form[f]).trim() === '');
    if (faltantes.length > 0) {
      setError('Completa todos los campos obligatorios marcados con *');
      setSaving(false);
      return;
    }

    try {
      const {
        nombre, apellido, email, telefono,
        sexo, fecha_nacimiento, lugar_nacimiento, nacionalidad,
        direccion, referencia_direccion, departamento, provincia, distrito,
        estado_civil,
        situacion_laboral, tipo_empleado, empresa_laboral, profesion_oficio,
      } = form;
      await perfilApi.updatePerfil({
        nombre, apellido, email, telefono,
        sexo, fecha_nacimiento, lugar_nacimiento, nacionalidad,
        direccion, referencia_direccion, departamento, provincia, distrito,
        estado_civil,
        situacion_laboral, tipo_empleado, empresa_laboral, profesion_oficio,
      });
      setSuccess('Perfil actualizado correctamente.');
      setCompleto(true);
      updateUser({ nombre: form.nombre, apellido: form.apellido, perfilCompleto: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePwChange = (e) =>
    setPwForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handlePwSave = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');
    if (pwForm.nueva_password !== pwForm.confirmar) {
      setPwError('Las contraseñas no coinciden.'); return;
    }
    setPwLoading(true);
    try {
      await perfilApi.cambiarPassword({
        password_actual: pwForm.password_actual,
        nueva_password: pwForm.nueva_password,
      });
      setPwSuccess('Contraseña actualizada correctamente.');
      setPwForm({ password_actual: '', nueva_password: '', confirmar: '' });
    } catch (err) {
      setPwError(err.message);
    } finally {
      setPwLoading(false);
    }
  };

  if (loading) return <div className="dash-loading"><div className="spinner"></div></div>;

  return (
    <div>
      <h2 className="dash-section-title">Mi Perfil</h2>
      <p className="dash-section-sub">Gestiona tu información personal y de seguridad</p>

      <div className="perfil-tabs">
        <button className={`perfil-tab${tab === 'datos' ? ' perfil-tab--active' : ''}`}
          onClick={() => setTab('datos')}>
          <i className="fa-solid fa-user"></i> Datos personales
        </button>
        <button className={`perfil-tab${tab === 'password' ? ' perfil-tab--active' : ''}`}
          onClick={() => setTab('password')}>
          <i className="fa-solid fa-lock"></i> Contraseña
        </button>
      </div>

      {tab === 'datos' && (
        <div className="dash-card">
          {error && <div className="dash-alert dash-alert--error">{error}</div>}
          {success && <div className="dash-alert dash-alert--success">{success}</div>}

          {!completo && (
            <div className="perfil-aviso">
              <i className="fa-solid fa-triangle-exclamation"></i>
              <div>
                <strong>Completa tu perfil</strong>
                <p>Para solicitar un crédito necesitas completar todos tus datos personales. Los campos marcados con <b>*</b> son obligatorios.</p>
              </div>
            </div>
          )}

          {completo && (
            <div className="perfil-completo-badge">
              <i className="fa-solid fa-circle-check"></i>
              Tus datos personales están completos. Si necesitas modificarlos, comunícate con nosotros.
            </div>
          )}

          <form onSubmit={handleSave} className="perfil-form">
            <div className="perfil-grid">
              {[
                { name: 'nombre', label: 'Nombre', icon: 'fa-user', required: true },
                { name: 'apellido', label: 'Apellidos', icon: 'fa-user', required: true },
                { name: 'email', label: 'Correo electrónico', icon: 'fa-envelope', type: 'email', required: true },
                { name: 'telefono', label: 'Teléfono', icon: 'fa-phone', required: true },
                { name: 'fecha_nacimiento', label: 'Fecha de nacimiento', icon: 'fa-calendar', type: 'date' },
                { name: 'lugar_nacimiento', label: 'Lugar de nacimiento', icon: 'fa-map-marker-alt' },
                { name: 'nacionalidad', label: 'Nacionalidad', icon: 'fa-flag' },
                { name: 'direccion', label: 'Dirección', icon: 'fa-map-marker-alt', required: true },
                { name: 'referencia_direccion', label: 'Referencia de dirección', icon: 'fa-sign-hanging' },
                { name: 'departamento', label: 'Departamento', icon: 'fa-map', required: true },
                { name: 'provincia', label: 'Provincia', icon: 'fa-map', required: true },
                { name: 'distrito', label: 'Distrito', icon: 'fa-map-pin', required: true },
                { name: 'empresa_laboral', label: 'Empresa laboral', icon: 'fa-building' },
                { name: 'profesion_oficio', label: 'Profesión / Oficio', icon: 'fa-briefcase' },
              ].map(({ name, label, icon, type = 'text', required: req }) => (
                <div className={`dash-field${req && !form[name] && !completo ? ' dash-field--missing' : ''}`} key={name}>
                  <label>{label}{req ? ' *' : ''}</label>
                  <div className="dash-input-wrap">
                    <i className={`fa-solid ${icon}`}></i>
                    <input type={type} name={name} value={form[name] || ''}
                      onChange={handleChange} placeholder={label}
                      disabled={completo} readOnly={completo}
                      required={req} />
                  </div>
                </div>
              ))}

              <div className="dash-field">
                <label>Sexo</label>
                <div className="dash-input-wrap">
                  <i className="fa-solid fa-venus-mars"></i>
                  <select name="sexo" value={form.sexo || ''} onChange={handleChange}
                    disabled={completo}>
                    <option value="">Seleccionar</option>
                    {SEXO.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              <div className={`dash-field${!form.estado_civil && !completo ? ' dash-field--missing' : ''}`}>
                <label>Estado civil *</label>
                <div className="dash-input-wrap">
                  <i className="fa-solid fa-heart"></i>
                  <select name="estado_civil" value={form.estado_civil || ''} onChange={handleChange}
                    disabled={completo}>
                    <option value="">Seleccionar</option>
                    {ESTADO_CIVIL.map((e) => <option key={e}>{e}</option>)}
                  </select>
                </div>
              </div>

              <div className={`dash-field${!form.situacion_laboral && !completo ? ' dash-field--missing' : ''}`}>
                <label>Situación laboral *</label>
                <div className="dash-input-wrap">
                  <i className="fa-solid fa-briefcase"></i>
                  <select name="situacion_laboral" value={form.situacion_laboral || ''} onChange={handleChange}
                    disabled={completo}>
                    <option value="">Seleccionar</option>
                    {SITUACION.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="dash-field">
                <label>Tipo de empleado</label>
                <div className="dash-input-wrap">
                  <i className="fa-solid fa-id-badge"></i>
                  <select name="tipo_empleado" value={form.tipo_empleado || ''} onChange={handleChange}
                    disabled={completo}>
                    <option value="">Seleccionar</option>
                    {TIPO_EMPLEADO.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {!completo && (
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? <span className="spinner-sm"></span> : (
                  <><i className="fa-solid fa-floppy-disk"></i> Guardar cambios</>
                )}
              </button>
            )}
          </form>
        </div>
      )}

      {tab === 'password' && (
        <div className="dash-card" style={{ maxWidth: 460 }}>
          {pwError && <div className="dash-alert dash-alert--error">{pwError}</div>}
          {pwSuccess && <div className="dash-alert dash-alert--success">{pwSuccess}</div>}
          <form onSubmit={handlePwSave} className="perfil-form">
            {[
              { name: 'password_actual', label: 'Contraseña actual' },
              { name: 'nueva_password', label: 'Nueva contraseña' },
              { name: 'confirmar', label: 'Confirmar nueva contraseña' },
            ].map(({ name, label }) => (
              <div className="dash-field" key={name}>
                <label>{label}</label>
                <div className="dash-input-wrap">
                  <i className="fa-solid fa-lock"></i>
                  <input type="password" name={name} value={pwForm[name]}
                    onChange={handlePwChange} placeholder="••••••••" required minLength={6} />
                </div>
              </div>
            ))}
            <button type="submit" className="btn-primary" disabled={pwLoading}>
              {pwLoading ? <span className="spinner-sm"></span> : (
                <><i className="fa-solid fa-key"></i> Cambiar contraseña</>
              )}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Perfil;
